import { createContext, useCallback, useContext, useMemo, useState } from "react"

import type { RemovedExtraService } from "../../form/formTypes"

type RemovedServicesContextValue = Readonly<{
    removedServices: RemovedExtraService[]
    addRemovedServices: (services: RemovedExtraService[]) => void
}>

const RemovedServicesContext = createContext<RemovedServicesContextValue | null>(null)

export function RemovedServicesProvider({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
    const [removedServices, setRemovedServices] = useState<RemovedExtraService[]>([])

    const addRemovedServices = useCallback((services: RemovedExtraService[]) => {
        setRemovedServices((prev) => {
            const existingIds = new Set(prev.map((r) => r.id))
            const unique = services.filter((s) => !existingIds.has(s.id))
            return unique.length > 0 ? [...prev, ...unique] : prev
        })
    }, [])

    const value = useMemo(() => ({ removedServices, addRemovedServices }), [removedServices, addRemovedServices])

    return <RemovedServicesContext.Provider value={value}>{children}</RemovedServicesContext.Provider>
}

export function useRemovedServices(): RemovedServicesContextValue {
    const ctx = useContext(RemovedServicesContext)
    if (!ctx) throw new Error("useRemovedServices must be used within RemovedServicesProvider")
    return ctx
}
